import { Controller } from '@hotwired/stimulus';
import { AsYouType, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js';

export default class extends Controller {
  static targets = ['countryCode', 'phoneNumber', 'errorMessage', 'submitButton'];
  
  connect() {
    console.log('Phone number controller connected');

    // Auto-detect user's country and pre-select country code
    this.autoDetectCountry();

    // Initialize with current country code
    this.currentCountryCode = this.getCountryCode();
    this.asYouType = new AsYouType(this.getCountryFromCode());

    // Set initial placeholder with country code
    this.updatePlaceholder();

    // Add event listeners
    this.setupEventListeners();

    // If there's an existing value, format it
    if (this.phoneNumberTarget.value) {
      this.formatExistingNumber();
    }
  }
  
  disconnect() {
    // Clean up event listeners if needed
  }

  autoDetectCountry() {
    if (!this.hasCountryCodeTarget) return;

    const country = this.detectUserCountry();
    if (!country) return;

    try {
      const callingCode = `+${getCountryCallingCode(country)}`;
      const options = this.countryCodeTarget.options;
      for (let i = 0; i < options.length; i++) {
        if (options[i].value === callingCode) {
          this.countryCodeTarget.selectedIndex = i;
          break;
        }
      }
    } catch (e) {
      // Country not supported by libphonenumber, keep default
    }
  }

  detectUserCountry() {
    // Method 1: Try navigator.language region (e.g. "en-US" → "US", "sr-RS" → "RS")
    try {
      const languages = navigator.languages || [navigator.language];
      for (const lang of languages) {
        if (lang && lang.includes('-')) {
          const region = lang.split('-').pop().toUpperCase();
          if (region && region.length === 2) {
            return region;
          }
        }
      }
    } catch (e) { /* ignore */ }

    // Method 2: Fall back to timezone
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return this.timezoneToCountry(tz);
    } catch (e) { /* ignore */ }

    return null;
  }

  timezoneToCountry(tz) {
    const map = {
      'America/New_York': 'US', 'America/Chicago': 'US', 'America/Denver': 'US',
      'America/Los_Angeles': 'US', 'America/Phoenix': 'US', 'America/Anchorage': 'US',
      'America/Adak': 'US', 'America/Boise': 'US', 'America/Detroit': 'US',
      'America/Indiana/Indianapolis': 'US', 'America/Juneau': 'US',
      'Pacific/Honolulu': 'US',
      'America/Toronto': 'CA', 'America/Vancouver': 'CA', 'America/Edmonton': 'CA',
      'America/Winnipeg': 'CA', 'America/Halifax': 'CA', 'America/St_Johns': 'CA',
      'America/Regina': 'CA',
      'America/Mexico_City': 'MX', 'America/Cancun': 'MX', 'America/Tijuana': 'MX',
      'America/Sao_Paulo': 'BR', 'America/Fortaleza': 'BR', 'America/Manaus': 'BR',
      'America/Argentina/Buenos_Aires': 'AR',
      'America/Santiago': 'CL', 'America/Bogota': 'CO', 'America/Lima': 'PE',
      'America/Caracas': 'VE',
      'Europe/London': 'GB', 'Europe/Paris': 'FR', 'Europe/Berlin': 'DE',
      'Europe/Rome': 'IT', 'Europe/Madrid': 'ES', 'Europe/Dublin': 'IE',
      'Europe/Amsterdam': 'NL', 'Europe/Brussels': 'BE', 'Europe/Zurich': 'CH',
      'Europe/Vienna': 'AT', 'Europe/Stockholm': 'SE', 'Europe/Oslo': 'NO',
      'Europe/Copenhagen': 'DK', 'Europe/Helsinki': 'FI', 'Europe/Warsaw': 'PL',
      'Europe/Prague': 'CZ', 'Europe/Budapest': 'HU', 'Europe/Athens': 'GR',
      'Europe/Lisbon': 'PT', 'Europe/Istanbul': 'TR', 'Europe/Moscow': 'RU',
      'Europe/Belgrade': 'RS', 'Europe/Bucharest': 'RO', 'Europe/Sofia': 'BG',
      'Europe/Zagreb': 'HR', 'Europe/Kiev': 'UA', 'Europe/Kyiv': 'UA',
      'Europe/Vilnius': 'LT', 'Europe/Riga': 'LV', 'Europe/Tallinn': 'EE',
      'Asia/Tokyo': 'JP', 'Asia/Shanghai': 'CN', 'Asia/Hong_Kong': 'HK',
      'Asia/Kolkata': 'IN', 'Asia/Calcutta': 'IN', 'Asia/Seoul': 'KR',
      'Asia/Singapore': 'SG', 'Asia/Kuala_Lumpur': 'MY', 'Asia/Manila': 'PH',
      'Asia/Bangkok': 'TH', 'Asia/Jakarta': 'ID', 'Asia/Ho_Chi_Minh': 'VN',
      'Asia/Dubai': 'AE', 'Asia/Riyadh': 'SA', 'Asia/Jerusalem': 'IL',
      'Asia/Tehran': 'IR', 'Asia/Karachi': 'PK', 'Asia/Dhaka': 'BD',
      'Asia/Colombo': 'LK', 'Asia/Kathmandu': 'NP', 'Asia/Taipei': 'TW',
      'Australia/Sydney': 'AU', 'Australia/Melbourne': 'AU', 'Australia/Brisbane': 'AU',
      'Australia/Perth': 'AU', 'Australia/Adelaide': 'AU',
      'Pacific/Auckland': 'NZ', 'Pacific/Fiji': 'FJ',
      'Africa/Johannesburg': 'ZA', 'Africa/Lagos': 'NG', 'Africa/Nairobi': 'KE',
      'Africa/Kampala': 'UG', 'Africa/Dar_es_Salaam': 'TZ', 'Africa/Accra': 'GH',
      'Africa/Douala': 'CM', 'Africa/Addis_Ababa': 'ET', 'Africa/Tunis': 'TN',
      'Africa/Casablanca': 'MA', 'Africa/Cairo': 'EG',
    };
    return map[tz] || null;
  }

  setupEventListeners() {
    // Listen to country code changes
    if (this.hasCountryCodeTarget) {
      this.countryCodeTarget.addEventListener('change', () => {
        this.handleCountryCodeChange();
      });
    }
    
    // Listen to phone number input
    if (this.hasPhoneNumberTarget) {
      // Format as user types
      this.phoneNumberTarget.addEventListener('input', (event) => {
        this.handlePhoneInput(event);
      });
      
      // Validate on blur
      this.phoneNumberTarget.addEventListener('blur', () => {
        this.validatePhoneNumber();
      });
      
      // Handle paste events
      this.phoneNumberTarget.addEventListener('paste', (event) => {
        setTimeout(() => this.handlePhoneInput(event), 0);
      });
    }
    
    // Intercept form submission
    const form = this.element.closest('form');
    if (form) {
      form.addEventListener('submit', (event) => {
        if (!this.validatePhoneNumber()) {
          event.preventDefault();
          event.stopPropagation();
          this.phoneNumberTarget.focus();
        } else {
          // Set the E.164 formatted number for backend submission
          const e164Number = this.phoneNumberTarget.dataset.e164;
          if (e164Number) {
            this.phoneNumberTarget.value = e164Number;
          }
        }
      });
    }
  }
  
  getCountryCode() {
    if (this.hasCountryCodeTarget) {
      return this.countryCodeTarget.value || '+1';
    }
    return '+1';
  }
  
  getCountryFromCode() {
    // Map common country codes to ISO country codes for libphonenumber
    const codeToCountry = {
      '+1': 'US',     // USA/Canada
      '+44': 'GB',    // UK
      '+33': 'FR',    // France
      '+49': 'DE',    // Germany
      '+39': 'IT',    // Italy
      '+34': 'ES',    // Spain
      '+61': 'AU',    // Australia
      '+81': 'JP',    // Japan
      '+86': 'CN',    // China
      '+91': 'IN',    // India
      '+7': 'RU',     // Russia
      '+55': 'BR',    // Brazil
      '+52': 'MX',    // Mexico
      '+27': 'ZA',    // South Africa
      '+234': 'NG',   // Nigeria
      '+254': 'KE',   // Kenya
      '+256': 'UG',   // Uganda
      '+255': 'TZ',   // Tanzania
      '+233': 'GH',   // Ghana
      '+237': 'CM',   // Cameroon
      '+251': 'ET',   // Ethiopia
      '+216': 'TN',   // Tunisia
      '+212': 'MA',   // Morocco
      '+20': 'EG',    // Egypt
      '+353': 'IE',   // Ireland
      '+31': 'NL',    // Netherlands
      '+32': 'BE',    // Belgium
      '+41': 'CH',    // Switzerland
      '+43': 'AT',    // Austria
      '+46': 'SE',    // Sweden
      '+47': 'NO',    // Norway
      '+45': 'DK',    // Denmark
      '+358': 'FI',   // Finland
      '+48': 'PL',    // Poland
      '+420': 'CZ',   // Czech Republic
      '+36': 'HU',    // Hungary
      '+30': 'GR',    // Greece
      '+351': 'PT',   // Portugal
      '+90': 'TR',    // Turkey
      '+82': 'KR',    // South Korea
      '+65': 'SG',    // Singapore
      '+60': 'MY',    // Malaysia
      '+63': 'PH',    // Philippines
      '+66': 'TH',    // Thailand
      '+62': 'ID',    // Indonesia
      '+84': 'VN',    // Vietnam
      '+64': 'NZ',    // New Zealand
      '+971': 'AE',   // UAE
      '+966': 'SA',   // Saudi Arabia
      '+972': 'IL',   // Israel
      '+98': 'IR',    // Iran
      '+92': 'PK',    // Pakistan
      '+880': 'BD',   // Bangladesh
      '+94': 'LK',    // Sri Lanka
      '+977': 'NP',   // Nepal
      '+381': 'RS',   // Serbia
    };
    
    const code = this.getCountryCode();
    return codeToCountry[code] || 'US';
  }
  
  updatePlaceholder() {
    if (!this.hasPhoneNumberTarget) return;
    
    const countryCode = this.getCountryCode();
    const baseText = 'Phone number';
    
    // Don't show country code in placeholder, just indicate format
    this.phoneNumberTarget.placeholder = baseText;
  }
  
  handleCountryCodeChange() {
    const newCountryCode = this.getCountryCode();
    const newCountry = this.getCountryFromCode();
    
    // Reset AsYouType formatter with new country
    this.asYouType = new AsYouType(newCountry);
    
    // If there's a number, reformat it with the new country code
    if (this.phoneNumberTarget.value) {
      const rawNumber = this.phoneNumberTarget.value.replace(/\D/g, '');
      if (rawNumber) {
        this.phoneNumberTarget.value = '';
        this.asYouType.input(rawNumber);
        this.phoneNumberTarget.value = this.asYouType.getNumber().nationalNumber || rawNumber;
      }
    }
    
    this.currentCountryCode = newCountryCode;
    this.updatePlaceholder();
    this.clearError();
  }
  
  handlePhoneInput(event) {
    const input = event.target;
    const cursorPosition = input.selectionStart;
    const previousValue = input.value;
    
    // Remove any non-digit characters except spaces, dashes, and parentheses
    let cleanValue = input.value.replace(/[^\d\s\-()]/g, '');
    
    // Remove country code if user typed it
    const countryCode = this.getCountryCode();
    const countryDigits = countryCode.replace('+', '');
    if (cleanValue.startsWith(countryDigits)) {
      cleanValue = cleanValue.substring(countryDigits.length);
    }
    if (cleanValue.startsWith('0' + countryDigits)) {
      cleanValue = cleanValue.substring(countryDigits.length + 1);
    }
    
    // Reset the formatter
    this.asYouType = new AsYouType(this.getCountryFromCode());
    
    // Format the number
    const formatted = this.asYouType.input(countryCode + cleanValue.replace(/\D/g, ''));
    
    // Get just the national part (without country code)
    const phoneNumber = this.asYouType.getNumber();
    if (phoneNumber && phoneNumber.nationalNumber) {
      // Format the national number
      const nationalFormatted = this.formatNationalNumber(phoneNumber.nationalNumber, this.getCountryFromCode());
      input.value = nationalFormatted;
    } else {
      // If parsing failed, just use cleaned value
      input.value = cleanValue;
    }
    
    // Try to maintain cursor position
    if (input.value.length >= previousValue.length) {
      const diff = input.value.length - previousValue.length;
      input.setSelectionRange(cursorPosition + diff, cursorPosition + diff);
    } else {
      input.setSelectionRange(cursorPosition, cursorPosition);
    }
    
    // Clear any previous error when user is typing
    this.clearError();
  }
  
  formatNationalNumber(nationalNumber, country) {
    // Format based on country
    const str = nationalNumber.toString();

    switch (country) {
      case 'US':
      case 'CA':
        // Format as (XXX) XXX-XXXX
        if (str.length === 10) {
          return `(${str.slice(0, 3)}) ${str.slice(3, 6)}-${str.slice(6)}`;
        } else if (str.length > 6) {
          return `(${str.slice(0, 3)}) ${str.slice(3, 6)}-${str.slice(6)}`;
        } else if (str.length > 3) {
          return `(${str.slice(0, 3)}) ${str.slice(3)}`;
        }
        break;
      case 'GB':
        // UK format
        if (str.length === 11 && str.startsWith('0')) {
          return `${str.slice(0, 5)} ${str.slice(5)}`;
        }
        break;
      case 'RS':
        // Serbian format: XX XXX XXXX (9 digits total)
        if (str.length === 9) {
          return `${str.slice(0, 2)} ${str.slice(2, 5)} ${str.slice(5)}`;
        } else if (str.length > 5) {
          return `${str.slice(0, 2)} ${str.slice(2, 5)} ${str.slice(5)}`;
        } else if (str.length > 2) {
          return `${str.slice(0, 2)} ${str.slice(2)}`;
        }
        break;
      default:
        // Default formatting with spaces every 3-4 digits
        if (str.length > 4) {
          return str.replace(/(\d{3,4})/g, '$1 ').trim();
        }
    }

    return str;
  }
  
  formatExistingNumber() {
    if (!this.phoneNumberTarget.value) return;
    
    const event = { target: this.phoneNumberTarget };
    this.handlePhoneInput(event);
  }
  
  validatePhoneNumber() {
    if (!this.hasPhoneNumberTarget) return true;
    
    const value = this.phoneNumberTarget.value;
    
    // If empty, consider it valid (unless required)
    if (!value || value.trim() === '') {
      if (this.phoneNumberTarget.required) {
        this.showError('Phone number is required');
        return false;
      }
      return true;
    }
    
    // Parse the complete phone number with country code
    const countryCode = this.getCountryCode();
    const fullNumber = countryCode + value.replace(/\D/g, '');
    
    try {
      const phoneNumber = parsePhoneNumberFromString(fullNumber);
      
      if (!phoneNumber) {
        this.showError('Invalid phone number format');
        return false;
      }
      
      if (!phoneNumber.isValid()) {
        this.showError('Invalid phone number for selected country');
        return false;
      }

      // Store the validated E.164 format in a data attribute
      this.phoneNumberTarget.dataset.e164 = phoneNumber.format('E.164');
      
      this.clearError();
      return true;
    } catch (error) {
      console.error('Phone validation error:', error);
      this.showError('Invalid phone number');
      return false;
    }
  }
  
  showError(message) {
    // Show error on the input
    this.phoneNumberTarget.classList.add('border-red-500', 'text-red-500');
    
    // Show error message if there's a target for it
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message;
      this.errorMessageTarget.classList.remove('hidden');
    } else {
      // Create and show an error message element
      const errorDiv = document.createElement('div');
      errorDiv.className = 'text-red-500 text-sm mt-1';
      errorDiv.textContent = message;
      errorDiv.dataset.phoneNumberTarget = 'errorMessage';
      this.phoneNumberTarget.parentElement.appendChild(errorDiv);
    }
    
    // Disable submit button if exists
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true;
      this.submitButtonTarget.classList.add('opacity-50', 'cursor-not-allowed');
    }
  }
  
  clearError() {
    // Remove error styling from input
    this.phoneNumberTarget.classList.remove('border-red-500', 'text-red-500');
    
    // Hide error message
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = '';
      this.errorMessageTarget.classList.add('hidden');
    }
    
    // Re-enable submit button if exists
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false;
      this.submitButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed');
    }
  }
  
  // Public method that can be called to get the full E.164 formatted number
  getFullPhoneNumber() {
    if (this.validatePhoneNumber()) {
      return this.phoneNumberTarget.dataset.e164 || '';
    }
    return '';
  }
}

